import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import ProjectDateReminderEntity from '@/entities/projectDateReminder';
import { catchGraphQlError } from '@/lib/catchError';

  import ProjectDateEntity from '@/entities/projectDate';
  import RoleEntity from '@/entities/role';

function ProjectDateReminderBaseUpdateForm({ formOnly = false, onClose, Entity = ProjectDateReminderEntity, id: propId, ...props }: BaseFormUpdateProps<typeof ProjectDateReminderEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = ProjectDateReminderEntity.model.useGet({
    variables: {
      id,
            withProjectDate: true,
            withRoles: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = ProjectDateReminderEntity.model.useUpdate();

  const relationsOptionsQuery = ProjectDateReminderEntity.model.useRelationsOptions({
    variables: {
          withProjectDates: true,
          withRoles: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              ProjectDateEntity,
              RoleEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:projectDateReminder') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateProjectDateReminder);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.projectDateReminder}
    />
  );
}

export default ProjectDateReminderBaseUpdateForm;
